<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import PageLayout from 'dashboard/components-next/metaAgent/PageLayout.vue';
import StatusCard from 'dashboard/components-next/metaAgent/StatusCard.vue';
import PlaygroundTurn from 'dashboard/components-next/metaAgent/PlaygroundTurn.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const agent = useMapGetter('metaAgent/getAgent');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const turns = ref([]);
const draft = ref('');
const conversationId = ref(null);
const transcript = ref(null);

const inboxId = computed(() => Number(route.params.inboxId));
const isOnboarded = computed(
  () => Boolean(agent.value.id) && agent.value.lifecycle !== 'pending_setup'
);
const canSend = computed(
  () => draft.value.trim().length > 0 && !uiFlags.value.sendingPlaygroundMessage
);

// Meta bills nothing for a test turn but still reports what a real one would
// have cost, so the running total is an estimate for the whole thread.
const totalTokens = computed(() =>
  turns.value.reduce((sum, turn) => sum + (turn.estimated_token_usage || 0), 0)
);

const scrollToLatest = () => {
  nextTick(() => {
    if (transcript.value) {
      transcript.value.scrollTop = transcript.value.scrollHeight;
    }
  });
};

const load = async () => {
  await store.dispatch('metaAgent/getInboxes');
  await store.dispatch('metaAgent/getAgent', inboxId.value);
};

const send = async () => {
  if (!canSend.value) return;

  const userMsg = draft.value.trim();
  draft.value = '';
  turns.value.push({ role: 'user', text: userMsg });
  scrollToLatest();

  try {
    const turn = await store.dispatch('metaAgent/sendPlaygroundMessage', {
      inboxId: inboxId.value,
      userMsg,
      conversationId: conversationId.value,
    });
    conversationId.value = turn.conversation_id;
    turns.value.push({ role: 'agent', ...turn });
  } catch (error) {
    const data = error?.response?.data || {};
    turns.value.push({
      role: 'error',
      text: data.detail || data.error || t('META_AGENT.PLAYGROUND.ERROR'),
    });
  }
  scrollToLatest();
};

// A new thread is the only way to test the agent without the previous turns
// steering it.
const reset = () => {
  turns.value = [];
  conversationId.value = null;
};

onMounted(load);
watch(inboxId, () => {
  reset();
  load();
});
</script>

<template>
  <PageLayout
    :header-title="t('META_AGENT.PLAYGROUND.HEADER')"
    :is-fetching="uiFlags.fetchingAgent"
    content-fill
  >
    <template #headerActions>
      <Button
        v-if="turns.length"
        :label="t('META_AGENT.PLAYGROUND.RESET')"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-rotate-ccw"
        @click="reset"
      />
    </template>

    <StatusCard
      v-if="!isOnboarded"
      variant="warning"
      :title="t('META_AGENT.OVERVIEW.ONBOARD.TITLE')"
      :description="t('META_AGENT.PLAYGROUND.NOT_ONBOARDED')"
    />
    <div v-else class="flex flex-col h-full gap-4">
      <div
        class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border rounded-xl bg-n-solid-1 border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('META_AGENT.PLAYGROUND.DESCRIPTION') }}
        </span>
        <div class="flex items-center gap-2">
          <span
            class="px-2 py-0.5 text-xs rounded-md bg-n-teal-3 text-n-teal-11"
          >
            {{ t('META_AGENT.PLAYGROUND.NOT_BILLED') }}
          </span>
          <span v-if="totalTokens" class="text-xs text-n-slate-11">
            {{
              t('META_AGENT.PLAYGROUND.ESTIMATED_TOKENS', {
                count: totalTokens,
              })
            }}
          </span>
        </div>
      </div>

      <div ref="transcript" class="flex flex-col flex-1 gap-3 overflow-y-auto">
        <span
          v-if="!turns.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('META_AGENT.PLAYGROUND.EMPTY') }}
        </span>
        <PlaygroundTurn
          v-for="(turn, index) in turns"
          :key="index"
          :turn="turn"
          @quick-reply="draft = $event"
        />
        <span
          v-if="uiFlags.sendingPlaygroundMessage"
          class="text-sm text-n-slate-10"
        >
          {{ t('META_AGENT.PLAYGROUND.THINKING') }}
        </span>
      </div>

      <!-- items-center, not items-end: the field is several times taller than
           the button, so hugging the bottom edge reads as misaligned even when
           the edges line up exactly. -->
      <div
        class="flex items-center flex-shrink-0 gap-2 pt-3 border-t border-n-weak"
      >
        <!-- The shared TextArea component neutralises the global `field-base`
             styling a raw <textarea> inherits (mb-4, border, outline). Its root
             is a plain div, so it carries no stray margin into the flex row. The
             keydown lives on the wrapper because it bubbles up from the field. -->
        <div class="flex-1 min-w-0" @keydown.enter.exact.prevent="send">
          <TextArea
            v-model="draft"
            :placeholder="t('META_AGENT.PLAYGROUND.PLACEHOLDER')"
            auto-height
            min-height="2.5rem"
            max-height="10rem"
          />
        </div>
        <Button
          :label="t('META_AGENT.PLAYGROUND.SEND')"
          size="sm"
          class="flex-shrink-0"
          :disabled="!canSend"
          :is-loading="uiFlags.sendingPlaygroundMessage"
          @click="send"
        />
      </div>
    </div>
  </PageLayout>
</template>
